import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Sparkles, Bot } from 'lucide-react';
import { Restaurant } from '../types';
import { mockRestaurants } from '../utils/mockData';
interface AskAIProps {
  restaurant?: Restaurant | null;
  isOpen: boolean;
  onClose: () => void;
  initialQuestion?: string;
}
interface Message {
  id: string;
  role: 'user' | 'ai';
  content: string;
}
export function AskAI({
  restaurant,
  isOpen,
  onClose,
  initialQuestion
}: AskAIProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasInitialized = useRef(false);
  const isGeneral = !restaurant;
  const suggestedQuestions = isGeneral ? ['Which restaurants have critical violations?', "What's the safest sushi place?", 'Any restaurants recently shut down?', 'Show me A-rated restaurants'] : ['Is this restaurant safe to eat at?', 'What were their recent issues?', 'Have they improved over time?'];
  // Reset messages when opening or switching context
  useEffect(() => {
    if (isOpen) {
      const welcomeContent = isGeneral ? "Hi! I'm the Franklin County Health Department AI assistant. I can help you find safe restaurants, understand inspection results, or answer questions about food safety in the county. What would you like to know?" : `Hi! I've analyzed the health inspection records for ${restaurant.name}. What would you like to know?`;
      setMessages([{
        id: 'welcome',
        role: 'ai',
        content: welcomeContent
      }]);
      hasInitialized.current = false;
    }
  }, [isOpen, restaurant?.id]);
  // Handle initial question from search bar
  useEffect(() => {
    if (isOpen && initialQuestion && !hasInitialized.current) {
      hasInitialized.current = true;
      // Small delay so the welcome message renders first
      setTimeout(() => {
        handleSend(initialQuestion);
      }, 400);
    }
  }, [isOpen, initialQuestion]);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, isTyping]);
  const generateGeneralResponse = (query: string): string => {
    const lowerQuery = query.toLowerCase();
    if (lowerQuery.includes('critical') || lowerQuery.includes('violation') || lowerQuery.includes('worst')) {
      const critical = mockRestaurants.filter(r => r.currentGrade === 'F' || r.currentGrade === 'C');
      if (critical.length === 0) return 'Good news — no restaurants currently have critical-level grades.';
      return `There are ${critical.length} restaurants with concerning grades:\n\n${critical.map(r => `• **${r.name}** (${r.cuisine}) — Grade ${r.currentGrade}, ${r.inspections[0]?.violations.length || 0} violations`).join('\n')}\n\nTap on any restaurant on the map to see the full details.`;
    }
    if (lowerQuery.includes('sushi') || lowerQuery.includes('japanese')) {
      const sushi = mockRestaurants.filter(r => r.cuisine === 'Japanese');
      if (sushi.length === 0) return "I don't have any Japanese restaurants in the current database.";
      return `I found ${sushi.length} Japanese restaurant(s):\n\n${sushi.map(r => `• **${r.name}** — Grade ${r.currentGrade} (Score: ${r.inspections[0]?.score}/100)`).join('\n')}\n\nLooks like a solid choice!`;
    }
    if (lowerQuery.includes('shut down') || lowerQuery.includes('closed') || lowerQuery.includes('fail')) {
      const failed = mockRestaurants.filter(r => r.inspections.some(i => i.status === 'Fail'));
      if (failed.length === 0) return 'No restaurants have been shut down recently.';
      return `${failed.length} restaurant(s) have had a failed inspection:\n\n${failed.map(r => `• **${r.name}** — received a failing grade on ${new Date(r.inspections.find(i => i.status === 'Fail')!.date).toLocaleDateString()}`).join('\n')}`;
    }
    if (lowerQuery.includes('a-rated') || lowerQuery.includes('grade a') || lowerQuery.includes('safest') || lowerQuery.includes('best')) {
      const aRated = mockRestaurants.filter(r => r.currentGrade === 'A');
      return `There are ${aRated.length} A-rated restaurants in Franklin County:\n\n${aRated.map(r => `• **${r.name}** (${r.cuisine}) — Score: ${r.inspections[0]?.score}/100`).join('\n')}\n\nAll excellent choices!`;
    }
    if (lowerQuery.includes('how') && lowerQuery.includes('grade')) {
      return 'Restaurants are graded on a scale of 0-100 based on health inspections:\n\n• **A** (90-100): Excellent compliance\n• **B** (80-89): Good, with minor issues\n• **C** (70-79): Needs improvement\n• **F** (Below 70): Critical issues, possible closure\n\nInspectors check food temperatures, cleanliness, pest control, employee hygiene, and more.';
    }
    return `Based on Franklin County health records, there are ${mockRestaurants.length} restaurants tracked. ${mockRestaurants.filter(r => r.currentGrade === 'A').length} have an A grade, and ${mockRestaurants.filter(r => r.currentGrade === 'F').length} have critical issues. You can ask me about specific cuisines, safety concerns, or any restaurant you see on the map.`;
  };
  const generateRestaurantResponse = (query: string): string => {
    if (!restaurant) return '';
    const lowerQuery = query.toLowerCase();
    const latest = restaurant.inspections[0];
    const violationCount = latest?.violations.length || 0;
    if (lowerQuery.includes('safe')) {
      if (restaurant.currentGrade === 'A') {
        return `Yes, ${restaurant.name} is considered very safe. They currently hold an 'A' grade with a score of ${latest?.score}/100 on their last inspection.`;
      } else if (restaurant.currentGrade === 'B') {
        return `${restaurant.name} is generally safe, holding a 'B' grade. However, they did have ${violationCount} issues during their last inspection that needed addressing.`;
      } else {
        return `Caution is advised. ${restaurant.name} currently holds a '${restaurant.currentGrade}' grade due to critical violations found during their recent inspection.`;
      }
    }
    if (lowerQuery.includes('issue') || lowerQuery.includes('violation')) {
      if (violationCount === 0) {
        return `Great news! They had zero violations during their last inspection on ${new Date(restaurant.lastInspectionDate).toLocaleDateString()}.`;
      }
      const criticals = latest.violations.filter(v => v.severity === 'Critical').length;
      return `During their last inspection, they had ${violationCount} total violations. ${criticals > 0 ? `${criticals} of these were considered critical.` : 'None of these were considered critical.'} You can see the full breakdown in the violations section.`;
    }
    if (lowerQuery.includes('improve') || lowerQuery.includes('history')) {
      if (restaurant.inspections.length < 2) return `I only have one recent inspection on record for ${restaurant.name}, so I can't determine a trend yet.`;
      const prev = restaurant.inspections[1];
      if (latest.score > prev.score) {
        return `Yes, they are improving! Their score went up from ${prev.score} to ${latest.score} in their most recent inspection.`;
      } else if (latest.score < prev.score) {
        return `Their score has actually dropped recently, going from ${prev.score} down to ${latest.score}.`;
      }
      return `Their performance has been stable, maintaining a score around ${latest.score}.`;
    }
    return `Based on health department data, ${restaurant.name} has a grade of ${restaurant.currentGrade}. They were last inspected on ${new Date(restaurant.lastInspectionDate).toLocaleDateString()}.`;
  };
  const handleSend = (text: string) => {
    if (!text.trim()) return;
    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text
    };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);
    setTimeout(() => {
      const response = isGeneral ? generateGeneralResponse(text) : generateRestaurantResponse(text);
      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'ai',
        content: response
      };
      setMessages(prev => [...prev, aiMsg]);
      setIsTyping(false);
    }, 1200);
  };
  return <AnimatePresence data-id="element-8">
      {isOpen && <motion.div initial={{
      opacity: 0,
      y: 100,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 100,
      scale: 0.95
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className="fixed bottom-0 sm:bottom-6 right-0 sm:right-6 w-full sm:w-[400px] h-[80vh] sm:h-[600px] bg-white sm:rounded-2xl shadow-2xl z-[70] flex flex-col border border-slate-200 overflow-hidden" data-id="element-9">
          {/* Header */}
          <div className="bg-teal-700 text-white p-4 flex items-center justify-between shrink-0" data-id="element-10">
            <div className="flex items-center gap-2.5" data-id="element-11">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center" data-id="element-12">
                <Bot className="w-5 h-5" data-id="element-13" />
              </div>
              <div data-id="element-14">
                <h3 className="font-bold text-sm" data-id="element-15">Health Inspector AI</h3>
                <p className="text-teal-200 text-xs" data-id="element-16">
                  {isGeneral ? 'Franklin County' : restaurant?.name}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-teal-600 rounded-full transition-colors" aria-label="Close chat" data-id="element-17">
              <X className="w-5 h-5" data-id="element-18" />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50" data-id="element-19">
            {messages.map(msg => <motion.div key={msg.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-20">
                <div className={`max-w-[85%] rounded-2xl p-3 text-sm whitespace-pre-line ${msg.role === 'user' ? 'bg-teal-600 text-white rounded-tr-sm' : 'bg-white text-slate-800 ring-1 ring-slate-200 rounded-tl-sm shadow-sm'}`} data-id="element-21">
                  {msg.content}
                </div>
              </motion.div>)}

            {isTyping && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="flex justify-start" data-id="element-22">
                <div className="bg-white ring-1 ring-slate-200 rounded-2xl rounded-tl-sm p-4 shadow-sm flex gap-1.5" data-id="element-23">
                  <motion.div className="w-2 h-2 bg-slate-400 rounded-full" animate={{
              y: [0, -5, 0]
            }} transition={{
              repeat: Infinity,
              duration: 0.6,
              delay: 0
            }} data-id="element-24" />
                  <motion.div className="w-2 h-2 bg-slate-400 rounded-full" animate={{
              y: [0, -5, 0]
            }} transition={{
              repeat: Infinity,
              duration: 0.6,
              delay: 0.2
            }} data-id="element-25" />
                  <motion.div className="w-2 h-2 bg-slate-400 rounded-full" animate={{
              y: [0, -5, 0]
            }} transition={{
              repeat: Infinity,
              duration: 0.6,
              delay: 0.4
            }} data-id="element-26" />
                </div>
              </motion.div>}
            <div ref={messagesEndRef} data-id="element-27" />
          </div>

          {/* Input Area */}
          <div className="p-4 bg-white border-t border-slate-200 shrink-0" data-id="element-28">
            {messages.length === 1 && <div className="flex flex-wrap gap-2 mb-4" data-id="element-29">
                {suggestedQuestions.map((q, i) => <button key={i} onClick={() => handleSend(q)} className="text-xs bg-teal-50 text-teal-700 px-3 py-1.5 rounded-full border border-teal-100 hover:bg-teal-100 transition-colors text-left" data-id="element-30">
                    {q}
                  </button>)}
              </div>}

            <form onSubmit={e => {
          e.preventDefault();
          handleSend(inputValue);
        }} className="flex items-center gap-2" data-id="element-31">
              <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder={isGeneral ? 'Ask about restaurants, safety...' : 'Ask about safety, violations...'} className="flex-1 bg-slate-100 border-transparent focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500 rounded-full px-4 py-2.5 text-sm transition-all" data-id="element-32" />
              <button type="submit" disabled={!inputValue.trim() || isTyping} className="p-2.5 bg-teal-600 text-white rounded-full hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-33">
                <Send className="w-4 h-4" data-id="element-34" />
              </button>
            </form>
          </div>
        </motion.div>}
    </AnimatePresence>;
}